// Reinsurance claim detail — full record + inline edit + validation
// panel + claim closings. Structurally mirrors DealDetail so the two
// screens feel consistent.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import useConfirm from '../../components/ConfirmDialog/useConfirm';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const C = {
  ink:      '#1e293b',
  muted:    '#64748b',
  border:   '#e2e8f0',
  soft:     '#f8fafc',
  burgundy: '#800020',
};

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '24px 40px', minHeight: 0 },
  crumbs:  { fontSize: 12, color: C.muted, marginBottom: 6 },
  crumbLink:{ color: C.muted, textDecoration: 'none', cursor: 'pointer' },
  head:    { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 4 },
  h1:      { fontSize: 22, fontWeight: 700, color: C.ink },
  sub:     { color: C.muted, fontSize: 13, margin: '4px 0 20px' },
  meta:    { fontSize: 11, color: C.muted },
  btn:     { padding: '9px 16px', borderRadius: 10, border: 'none', background: C.burgundy, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  btnDis:  { padding: '9px 16px', borderRadius: 10, border: 'none', background: '#cbd5e1', color: '#fff', cursor: 'not-allowed', fontWeight: 600, fontSize: 13 },
  ghost:   { padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  danger:  { padding: '9px 16px', borderRadius: 10, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  card:    { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, marginBottom: 16 },
  section: { fontSize: 12, fontWeight: 700, color: C.burgundy, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 },
  grid:    { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 },
  field:   { display: 'flex', flexDirection: 'column', gap: 4 },
  label:   { fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6 },
  input:   { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.ink, fontFamily: 'inherit' },
  ta:      { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.ink, minHeight: 78, resize: 'vertical', fontFamily: 'inherit' },
  ro:      { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.soft, color: C.ink },
  pill:    (status) => ({
    display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: status === 'paid' ? '#dcfce7' : status === 'declined' ? '#fee2e2' : status === 'closed' ? '#e2e8f0' : '#fef3c7',
    color:      status === 'paid' ? '#166534' : status === 'declined' ? '#991b1b' : status === 'closed' ? '#475569' : '#92400e',
  }),
  checkPill: (status) => ({
    display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, marginRight: 8,
    background: status === 'fail' ? '#fee2e2' : status === 'warn' ? '#fef3c7' : '#dcfce7',
    color:      status === 'fail' ? '#991b1b' : status === 'warn' ? '#92400e' : '#166534',
  }),
  table:   { width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 12 },
  th:      { textAlign: 'left', padding: '8px 10px', background: C.soft, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` },
  td:      { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  err:  { color: '#dc2626', fontSize: 13, marginTop: 8 },
  ok:   { color: '#166534', fontSize: 13, marginTop: 8 },
};

const STATUS_OPTIONS = ['notified', 'under_review', 'agreed', 'paid', 'declined', 'closed'];
const NUMERIC = ['claimedAmount', 'reserveAmount', 'paidAmount'];

const toDateInputValue = (v) => { if (!v) return ''; try { return new Date(v).toISOString().slice(0, 10); } catch (e) { return String(v).slice(0, 10); } };
const fmt = (v) => (v == null || v === '' ? '—' : v);
const fmtMoney = (n, ccy) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try { return `${ccy || ''} ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim(); }
  catch (e) { return String(n); }
};
const fmtPct = (n) => (n == null || n === '' ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 3 })}%`);
const fmtDateTime = (v) => { if (!v) return '—'; try { const d = new Date(v); return `${d.toISOString().slice(0,10)} ${d.toISOString().slice(11,16)} UTC`; } catch (e) { return String(v); } };

function hydrateForm(c) {
  return {
    claimReference:  c.claimReference || '',
    dateOfLoss:      c.dateOfLoss || '',
    dateReported:    c.dateReported || '',
    causeOfLoss:     c.causeOfLoss || '',
    description:     c.description || '',
    claimedAmount:   c.claimedAmount ?? '',
    reserveAmount:   c.reserveAmount ?? '',
    paidAmount:      c.paidAmount ?? '',
    currency:        c.currency || '',
    status:          c.status || 'notified',
    notes:           c.notes || '',
  };
}
function buildPayload(form) {
  const out = { ...form };
  NUMERIC.forEach((k) => {
    if (out[k] === '' || out[k] == null) delete out[k];
    else out[k] = Number(out[k]);
  });
  return out;
}

const ClaimDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { confirm, ConfirmPortal } = useConfirm();

  const [claim, setClaim]         = useState(null);
  const [form, setForm]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveOk, setSaveOk]       = useState(false);
  const [error, setError]         = useState(null);
  const [validating, setValidating] = useState(false);
  const [valError, setValError]   = useState(null);
  const [closingBusy, setClosingBusy] = useState(null);
  const [closingError, setClosingError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/claims/${id}`, { withCredentials: true });
      const c = resp?.data?.data;
      if (!c) { setError('Claim not found'); return; }
      setClaim(c);
      setForm(hydrateForm(c));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load claim');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  const onField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setError(null); setSaveOk(false);
    try {
      const resp = await axios.patch(`${BASE_URL}/reinsurance/claims/${id}`, buildPayload(form), { withCredentials: true });
      const c = resp?.data?.data;
      if (c) { setClaim(c); setForm(hydrateForm(c)); setSaveOk(true); setTimeout(() => setSaveOk(false), 2000); }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const validate = async () => {
    setValidating(true); setValError(null);
    try {
      const resp = await axios.post(`${BASE_URL}/reinsurance/claims/${id}/validate`, {}, { withCredentials: true });
      const snap = resp?.data?.data;
      if (snap) setClaim((prev) => ({ ...prev, validation: snap }));
    } catch (err) {
      setValError(err?.response?.data?.message || err.message || 'Validation failed');
    } finally { setValidating(false); }
  };

  const deleteClaim = async () => {
    const ok = await confirm({
      title: 'Delete reinsurance claim?',
      message: `${claim?.claimNumber || 'This claim'} will be permanently removed.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${BASE_URL}/reinsurance/claims/${id}`, { withCredentials: true });
      history.push('/reinsurance/claims');
    } catch (err) { setError(err?.response?.data?.message || err.message || 'Delete failed'); }
  };

  const generateClosing = async (kind, extras = {}) => {
    const busyKey = kind === 'reinsurer_debit' ? `rd:${extras.reinsurerIndex}` : kind;
    setClosingBusy(busyKey); setClosingError(null);
    const path = kind === 'reinsurer_debit' ? 'reinsurer-debit' : 'cedant-advice';
    try {
      const resp = await axios.post(
        `${BASE_URL}/reinsurance/claims/${id}/closings/${path}`,
        kind === 'reinsurer_debit' ? { reinsurerIndex: extras.reinsurerIndex } : {},
        { withCredentials: true },
      );
      const row = resp?.data?.data;
      if (row) setClaim((prev) => ({ ...prev, closings: [...(prev.closings || []), row] }));
    } catch (err) {
      setClosingError(err?.response?.data?.message || err.message || 'Generation failed');
    } finally { setClosingBusy(null); }
  };

  const downloadClosing = async (closingId) => {
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/claims/${id}/closings/${closingId}/download`, { withCredentials: true });
      const url = resp?.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) { setClosingError(err?.response?.data?.message || err.message || 'Download link failed'); }
  };

  if (loading) return <AppLayout><div style={S.page}><div style={{ color: C.muted }}>Loading claim…</div></div></AppLayout>;
  if (error && !claim) return (
    <AppLayout>
      <div style={S.page}>
        <div style={S.crumbs}>
          <span style={S.crumbLink} onClick={() => history.push('/reinsurance/claims')}>Reinsurance Claims</span>
          <span> / {id}</span>
        </div>
        <div style={S.err}>{error}</div>
      </div>
    </AppLayout>
  );

  const v = claim.validation;
  const hasValidation = !!v?.validatedAt;
  const failedChecks = (v?.checks || []).filter((c) => c.status === 'fail');
  const warnChecks   = (v?.checks || []).filter((c) => c.status === 'warn');
  const passChecks   = (v?.checks || []).filter((c) => c.status === 'pass');

  return (
    <AppLayout>
      <div style={S.page}>
        <div style={S.crumbs}>
          <span style={S.crumbLink} onClick={() => history.push('/reinsurance/claims')}>Reinsurance Claims</span>
          <span> / {claim.claimNumber || id}</span>
        </div>
        <div style={S.head}>
          <div>
            <div style={S.h1}>
              {claim.claimNumber || '(no number)'} <span style={{ marginLeft: 10 }}><span style={S.pill(claim.status)}>{(claim.status || '').replace('_', ' ')}</span></span>
            </div>
            <div style={S.meta}>
              Deal <b style={{ color: C.ink, cursor: 'pointer' }} onClick={() => history.push(`/reinsurance/deals/${claim.deal}`)}>{claim.dealNumber || String(claim.deal).slice(-8)}</b>
              {claim.claimReference && <> · Cedant Ref: <b style={{ color: C.ink }}>{claim.claimReference}</b></>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.ghost} onClick={() => history.push('/reinsurance/claims')}>← Back</button>
            <button style={S.danger} onClick={deleteClaim}>Delete</button>
            <button style={saving ? S.btnDis : S.btn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
        <p style={S.sub}>Loss reported against the linked reinsurance deal. Validation checks period-in-cover and market placement, then allocates the loss across signed reinsurers.</p>

        {/* Loss detail */}
        <div style={S.card}>
          <div style={S.section}>Loss Detail</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Cedant Claim Reference</label>
              <input style={S.input} value={form.claimReference} onChange={onField('claimReference')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Status</label>
              <select style={S.input} value={form.status} onChange={onField('status')}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>Date of Loss *</label>
              <input type='date' style={S.input} value={toDateInputValue(form.dateOfLoss)} onChange={onField('dateOfLoss')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Date Reported</label>
              <input type='date' style={S.input} value={toDateInputValue(form.dateReported)} onChange={onField('dateReported')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Cause of Loss</label>
              <input style={S.input} value={form.causeOfLoss} onChange={onField('causeOfLoss')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Currency</label>
              <input style={S.input} value={form.currency} onChange={onField('currency')} />
            </div>
            <div style={{ ...S.field, gridColumn: '1 / -1' }}>
              <label style={S.label}>Description</label>
              <textarea style={S.ta} value={form.description} onChange={onField('description')} />
            </div>
          </div>
        </div>

        {/* Amounts */}
        <div style={S.card}>
          <div style={S.section}>Amounts</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Claimed Amount</label>
              <input type='number' style={S.input} value={form.claimedAmount ?? ''} onChange={onField('claimedAmount')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Reserve Amount</label>
              <input type='number' style={S.input} value={form.reserveAmount ?? ''} onChange={onField('reserveAmount')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Paid Amount</label>
              <input type='number' style={S.input} value={form.paidAmount ?? ''} onChange={onField('paidAmount')} />
            </div>
          </div>
        </div>

        {/* Validation */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={S.section}>Validation</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {hasValidation && <span style={S.meta}>Last run: {fmtDateTime(v.validatedAt)}</span>}
              <button style={validating ? S.btnDis : S.btn} onClick={validate} disabled={validating}>
                {validating ? 'Validating…' : (hasValidation ? 'Revalidate' : 'Validate claim')}
              </button>
            </div>
          </div>
          {valError && <div style={S.err}>{valError}</div>}
          {!hasValidation && !valError && (
            <div style={{ padding: 16, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 13 }}>
              Not validated yet. Click <b>Validate claim</b> to check: loss is in-period, deal is placed with signed markets, and to compute per-reinsurer allocation.
            </div>
          )}
          {hasValidation && (
            <>
              {/* Check pills */}
              <div style={{ marginBottom: 12 }}>
                {(v.checks || []).map((chk, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <span style={S.checkPill(chk.status)}>{chk.status}</span>
                    <span style={{ fontSize: 13, color: C.ink }}>{chk.message}</span>
                  </div>
                ))}
              </div>
              {failedChecks.length === 0 && warnChecks.length === 0 && (
                <div style={{ padding: 10, background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 8, color: '#065f46', fontSize: 12 }}>
                  All {passChecks.length} checks passed. Claim is validated and ready for closings.
                </div>
              )}

              {/* Deal totals */}
              {v.dealTotals && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Deal Split</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                    {[
                      ['Claimed', v.dealTotals.claimedAmount],
                      ['Retained by cedant', v.dealTotals.retainedByCedant],
                      ['Ceded to markets', v.dealTotals.cededToMarkets],
                      ['Unallocated cession', v.dealTotals.unallocatedCession],
                    ].map(([label, val], i) => (
                      <div key={i} style={{ padding: '10px 12px', background: C.soft, borderRadius: 8, border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{fmtMoney(val, v.dealTotals.currency)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-market allocations */}
              {v.allocations && v.allocations.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Per-Market Allocation</div>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Reinsurer</th>
                        <th style={S.th}>Share</th>
                        <th style={S.th}>Currency</th>
                        <th style={S.th}>Amount owed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.allocations.map((a) => (
                        <tr key={a.reinsurerIndex}>
                          <td style={{ ...S.td, fontWeight: 600, color: C.ink }}>{a.reinsurerName}</td>
                          <td style={S.td}>{fmtPct(a.signedShare)}</td>
                          <td style={S.td}>
                            {a.currency || '—'}
                            {a.currency && v.dealTotals?.currency && a.currency !== v.dealTotals.currency && (
                              <span style={{ marginLeft: 6, fontSize: 10, color: C.muted }}>(FX at debit)</span>
                            )}
                          </td>
                          <td style={S.td}>{fmtMoney(a.amount, v.dealTotals?.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Claim closings */}
        <div style={S.card}>
          <div style={S.section}>Claim Closings</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            Tenant-branded PDFs from the validation snapshot. Cedant advice summarises the total collectable; per-reinsurer debit notes carry the specific share owed. Both require a validation run.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <button
              style={closingBusy === 'cedant_advice' || !hasValidation ? S.btnDis : S.btn}
              disabled={!!closingBusy || !hasValidation}
              onClick={() => generateClosing('cedant_advice')}
              title={!hasValidation ? 'Validate the claim first' : 'Advise cedant of total collectable + market allocation'}
            >
              {closingBusy === 'cedant_advice' ? 'Generating…' : 'Cedant advice'}
            </button>
          </div>
          {hasValidation && v.allocations && v.allocations.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Reinsurer debit notes</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {v.allocations.map((a) => {
                  const busy = closingBusy === `rd:${a.reinsurerIndex}`;
                  return (
                    <button
                      key={a.reinsurerIndex}
                      style={busy ? S.btnDis : S.ghost}
                      disabled={!!closingBusy}
                      onClick={() => generateClosing('reinsurer_debit', { reinsurerIndex: a.reinsurerIndex })}
                    >
                      {busy ? 'Generating…' : `Debit → ${a.reinsurerName}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {closingError && <div style={S.err}>{closingError}</div>}

          {claim.closings && claim.closings.length > 0 ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, margin: '14px 0 6px' }}>Generated</div>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Ref</th>
                    <th style={S.th}>Kind</th>
                    <th style={S.th}>Reinsurer</th>
                    <th style={S.th}>Filename</th>
                    <th style={S.th}>Generated</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {[...claim.closings].reverse().map((c) => (
                    <tr key={c._id}>
                      <td style={{ ...S.td, fontWeight: 600, color: C.burgundy }}>{c.ref || '—'}</td>
                      <td style={S.td}>{(c.kind || '').replace('_', ' ')}</td>
                      <td style={S.td}>{c.reinsurerName || '—'}</td>
                      <td style={S.td}>{c.filename || '—'}</td>
                      <td style={S.td}>{fmtDateTime(c.generatedAt)}</td>
                      <td style={S.td}>
                        <button style={{ ...S.ghost, padding: '4px 10px', fontSize: 12 }} onClick={() => downloadClosing(c._id)}>Download</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{ padding: 14, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 12 }}>No closings generated yet.</div>
          )}
        </div>

        {/* Notes */}
        <div style={S.card}>
          <div style={S.section}>Notes</div>
          <textarea style={S.ta} value={form.notes} onChange={onField('notes')} />
        </div>

        {error && <div style={S.err}>{error}</div>}
        {saveOk && <div style={S.ok}>Saved.</div>}
      </div>
      {ConfirmPortal}
    </AppLayout>
  );
};

export default ClaimDetail;
